'use client'

import { useK2Setup } from '@/eshop/hooks/useK2Setup'
import { getCategories } from '@/eshop/queries'
import { useQuery } from '@tanstack/react-query'
import { useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
    defaultCategory,
    EshopContext,
    EshopContextCategory,
} from '../EshopProvider'
import { Category } from '@/eshop/gql/graphql'
import { hydrateResult } from '@/eshop/utils/client'
import { SelectOption } from '@/components/FormComponents/types'
import { Select } from '@/components/FormComponents/select'
import { useUser } from '@/eshop/hooks/useUser'
import { ApiCategoryProductsResponse } from '@/eshop/types'

export default function EshopCategories() {
    const { categories, setCategories } = useContext(EshopContext)
    const k2 = useK2Setup()

    const handleSelectCategory = useCallback(
        (index: number): SectionProps['onSelect'] =>
            (c) => {
                const catName = c?.categoryLangItems?.find(
                    (l) => l?.languageId === k2.languageId
                )

                setCategories(
                    categories.map((cat, i) => {
                        if (i < index) {
                            return cat
                        } else if (i === index) {
                            return {
                                id: c?.id || null,
                                name: catName?.translatedTitleCalc || '---',
                                languageId: k2.languageId,
                            }
                        }
                        return { ...defaultCategory }
                    })
                )
            },
        [categories, setCategories, k2.languageId]
    )

    return (
        <div className="my-10 px-4">
            <style
                dangerouslySetInnerHTML={{
                    __html: `
                    .eshop-categories .rselect__indicator {
                        cursor: pointer;
                    }
                    .eshop-categories > label {
                      font-size: 13px;
                    }
                    .eshop-categories  .rselect__control {
                      border-color: #cbd5e0 !important;
                    }
                    .eshop-categories .rselect__dropdown-indicator {
                        border: 1px solid #cbd5e0;
                        border-radius: 999px;
                        margin-right: 4px;
                        transition: transform .5s;
                    }
              `,
                }}
            />
            <div className="grid grid-cols-1 sm:grid-cols-2 desktop:grid-cols-4 gap-4 justify-around">
                {categories.map((category, i, arr) => {
                    const parent = i === 0 ? null : arr[i - 1]
                    if (i > 0 && parent?.id === null) {
                        return null
                    }
                    return (
                        <Section
                            key={`${i}-${category.id}`}
                            index={i}
                            parentId={parent?.id || null}
                            selectedCategory={
                                category.id === null ? undefined : category
                            }
                            onSelect={handleSelectCategory(i)}
                        />
                    )
                })}
            </div>
        </div>
    )
}

interface SectionProps {
    parentId: string | null
    index: number
    selectedCategory: EshopContextCategory | undefined
    onSelect: (c: Category | null) => void
}

type CategoryData = Array<{ value: string; label: string }>

function Section({
    parentId,
    onSelect,
    selectedCategory,
    index,
}: SectionProps) {
    const k2 = useK2Setup()
    const { t } = useTranslation()
    const user = useUser()
    const [categoriesData, setCategoriesData] = useState<CategoryData>([])

    const { data: fetchedData } = useQuery({
        queryKey: ['categories', user?.id, parentId],
        queryFn: () =>
            getCategories()({
                filter: [
                    {
                        categoryLangItems_languageId: {
                            eq: k2.languageId,
                        },
                        parent_id: {
                            eq: parentId,
                        },
                        parent: {
                            isNull: !parentId,
                        },
                    },
                ],
                after: '',
                first: 100,
            }).toPromise(),
    })

    useEffect(() => {
        const newCategoriesData: CategoryData = []
        hydrateResult(fetchedData)?.match({
            Ok: async (r) => {
                const edges = r?.categories?.edges ?? []
                for (const e of edges) {
                    try {
                        const res = await fetch(
                            '/api/eshop/categories?categoryId=' +
                                e.node.id +
                                '&languageId=' +
                                k2.languageId
                        )
                        if (!res.ok) {
                            continue
                        }
                        const data =
                            (await res.json()) as ApiCategoryProductsResponse
                        if (data.hasProducts) {
                            newCategoriesData.push({
                                value: e.node.id,
                                label:
                                    e.node.categoryLangItems.find(
                                        (l) => l?.languageId === k2.languageId
                                    )?.translatedTitleCalc ?? '---',
                            })
                        }
                    } catch (err) {
                        console.log(
                            'ERROR: cannot load category data for ' +
                                e.node.id +
                                err
                        )
                    }
                }
                setCategoriesData(newCategoriesData)
            },
            Error: async (e) => {
                console.log(e)
            },
        })
    }, [fetchedData, k2.languageId])

    const data = useMemo(
        () =>
            hydrateResult(fetchedData)?.match({
                Ok: (r) => {
                    return r?.categories?.edges ?? []
                },
                Error: (e) => {
                    console.log(e)
                    return []
                },
            }),
        [fetchedData]
    )

    const selectOptions = useMemo(
        (): SelectOption[] =>
            data?.map((r) => {
                const title = r.node.categoryLangItems.find(
                    (l) => l?.languageId === k2.languageId
                )?.translatedTitleCalc

                return {
                    label: title ? `${title}` : '---',
                    value: r.node.id!,
                }
            }) || [],
        [data, k2.languageId]
    )

    if (selectOptions.length === 0) {
        return null
    }

    return (
        <Select
            isClearable
            placeholder={t(`eshop.categories.placeholder${index}`)}
            wrapperClassName="eshop-categories"
            label={t(`eshop.categories.label${index}`)}
            name={`category`}
            value={selectedCategory?.id || undefined}
            options={categoriesData}
            onChange={(newValue, meta) => {
                if (meta.action === 'clear') {
                    return onSelect(null)
                }
                const v = newValue as SelectOption
                if (v?.value) {
                    const c = data?.find((r) => r.node.id === v.value)
                    !!c && onSelect(c.node)
                }
            }}
        />
    )
}
